import { Button, Icon } from "@deligoo/ui"
import clsx from "clsx"
import {
  ComponentProps,
  forwardRef,
  HTMLAttributes,
  HTMLProps,
  ReactNode,
  RefObject,
  SetStateAction,
  useCallback,
  useState,
} from "react"

import cls from "./Panel.module.scss"

export const Panel = ({
  className,
  wrapperClassName,
  children,
}: {
  children: ReactNode
  className?: string
  wrapperClassName?: string
}) => {
  return (
    <section className={wrapperClassName}>
      <div className={clsx(cls.panel, className)}>{children}</div>
    </section>
  )
}

const Header = forwardRef(({ className, children, ...props }: HTMLAttributes<HTMLDivElement>, ref) => {
  return (
    <header className={clsx(cls.header, className)} ref={ref as RefObject<HTMLDivElement>}>
      {children}
    </header>
  )
})

const Title = ({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={clsx(cls.title, className)} {...props}>
      {children}
    </div>
  )
}

const Name = ({ className, children, ...props }: HTMLAttributes<HTMLHeadingElement>) => {
  return (
    <h1 className={clsx(cls.panelName, className)} {...props}>
      {children}
    </h1>
  )
}

const Tabs = ({ children }: { children: ReactNode }) => {
  return <div className={cls.panelTabs}>{children}</div>
}

const TabsDropdown = ({
  active,
  setActive,
}: {
  active: boolean
  setActive: (action: SetStateAction<boolean>) => void
}) => {
  return (
    <button
      title="Filtry"
      className={clsx(cls.tabsDropdown, active && cls.active)}
      onClick={() => setActive((isActive) => !isActive)}
    >
      <Icon size="small" name={active ? "chevron-tiny-down" : "chevron-tiny-up"} className={cls.icon} />
    </button>
  )
}

const Tab = ({
  label,
  value,
  active,
  onClick,
}: {
  label: ReactNode
  value: ReactNode
  active: boolean
  onClick: () => void
}) => {
  return (
    <button type="button" className={clsx(cls.tab, active && cls.active)} onClick={onClick}>
      <div className={cls.label}>{label}</div>
      <div className={cls.value}>{value}</div>
    </button>
  )
}

const SearchInput = ({ className, ...props }: HTMLProps<HTMLInputElement>) => {
  return <input className={clsx(cls.searchInput, className)} autoFocus {...props} />
}

const SearchButton = ({ isActive, className, ...props }: { isActive: boolean } & ComponentProps<typeof Button>) => {
  return (
    <Button
      iconProps={{
        name: isActive ? "close-tiny" : "search",
        color: isActive ? "muted" : "primary",
      }}
      variation={isActive ? "solid" : "contrast"}
      className={clsx(cls.searchButton, className)}
      size="small"
      {...props}
    />
  )
}

const Content = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <div className={clsx(cls.content, className)}>{children}</div>
}

export function usePanelTabs<T extends string | null>(defaultTab: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [currentTab, _setCurrentTab] = useState<T>(defaultTab)

  const setCurrentTab = useCallback(_setCurrentTab, [_setCurrentTab])

  return [currentTab, setCurrentTab]
}

Panel.Header = Header
Panel.Title = Title
Panel.Name = Name
Panel.Tabs = Tabs
Panel.TabsDropdown = TabsDropdown
Panel.Tab = Tab
Panel.SearchInput = SearchInput
Panel.SearchButton = SearchButton
Panel.Content = Content
